import {
  type MaintenanceTemplateDetail,
  type MaintenanceTemplateVersion,
} from '@gb-ticket/shared-types';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  maintenanceTemplateSourcePdfUrl,
  useUpdateMaintenanceTemplateVersion,
} from '@/products/maintenance/features/use-maintenance-templates';
import { ChecklistEditor } from '@/products/maintenance/features/templates/checklist-editor';
import { parseChecklistDefinition } from '@/products/maintenance/features/templates/checklist-editor-state';
import { PdfOverlayEditor } from '@/products/maintenance/features/templates/pdf-overlay/pdf-overlay-editor';
import {
  templateNeedsPdf,
  templatePdfPreviewText,
} from '@/products/maintenance/features/templates/template-kind-label';

type Props = {
  template: MaintenanceTemplateDetail;
  version: MaintenanceTemplateVersion;
};

export function TemplateVersionEditor({ template, version }: Props) {
  const savedJson = JSON.stringify(version.definition);
  const [definition, setDefinition] = useState(() => parseChecklistDefinition(version.definition));
  const [selectedFieldKey, setSelectedFieldKey] = useState<string | null>(null);
  const dirty = JSON.stringify(definition) !== savedJson;
  const save = useUpdateMaintenanceTemplateVersion(template.id, version.id);

  useEffect(() => {
    if (!dirty || version.status !== 'draft') {
      return;
    }
    const handle = window.setTimeout(() => {
      void save
        .mutateAsync({ definition })
        .then(() => toast.success('Gespeichert.'))
        .catch(() => toast.error('Die Definition konnte nicht gespeichert werden.'));
    }, 900);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- debounced snapshot of definition only
  }, [definition, dirty, version.status]);

  const pdfReady =
    version.hasSourcePdf &&
    version.sourcePdfScanStatus !== 'PENDING' &&
    version.sourcePdfScanStatus !== 'BLOCKED' &&
    version.pageCount != null;
  const pdfUrl = pdfReady ? maintenanceTemplateSourcePdfUrl(version.id) : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        {dirty ? (
          <span className="text-amber-600">
            Ungespeicherte Änderungen werden gleich gespeichert …
          </span>
        ) : (
          <span className="text-muted-foreground">Alle Änderungen sind gespeichert.</span>
        )}
        {save.isPending ? <span className="text-muted-foreground">Speichert …</span> : null}
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={!dirty || save.isPending}
          onClick={() => {
            void save.mutateAsync({ definition });
          }}
        >
          Jetzt speichern
        </Button>
      </div>

      {template.kind === 'pdf_ink' ? null : (
        <ChecklistEditor
          definition={definition}
          onChange={setDefinition}
          kind={template.kind}
          pdfPageCount={version.pageCount}
          selectedFieldKey={selectedFieldKey}
          onSelectFieldKey={setSelectedFieldKey}
        />
      )}

      {templateNeedsPdf(template.kind) ? (
        pdfUrl && version.pageCount ? (
          <section className="space-y-2">
            <h2 className="text-base font-semibold">PDF</h2>
            <PdfOverlayEditor
              pdfUrl={pdfUrl}
              definition={definition}
              pageCount={version.pageCount}
              selectedFieldKey={selectedFieldKey}
              onSelectField={setSelectedFieldKey}
            />
          </section>
        ) : (
          <p className="text-sm text-muted-foreground">
            {templatePdfPreviewText('version', version.hasSourcePdf)}
          </p>
        )
      ) : null}
    </div>
  );
}
